// Account-level custom order statuses (Settings > Sales Account > Order Statuses,
// persisted as `customOrderStatuses`). They are appended to the built-in status
// lists on purchase orders and schedules, so a studio can track stages the
// built-ins don't cover ("At Workroom", "In Customs", "Punch List", …).
//
// Statuses are stored as their own label — `po.status`, `item.status` and
// `scheduleItem.status` all hold the literal string — so there is no separate
// value/label pair to keep in sync (unlike freightDestinations.js).

export const BUILT_IN_PO_STATUSES = [
  'Draft',
  'Ordered',
  'Back Ordered',
  'Workroom',
  'En route',
  'Delivered',
  'Installed',
  'Cancelled',
];

// The create-a-PO modal opens on "No status" and doesn't offer Draft/Cancelled,
// which only make sense once the PO exists.
export const BUILT_IN_PO_INTAKE_STATUSES = [
  'No status',
  'Ordered',
  'Back Ordered',
  'Workroom',
  'En route',
  'Delivered',
  'Installed',
];

export const BUILT_IN_SCHEDULE_STATUSES = [
  'Draft',
  'Specified',
  'Ordered',
  'Delivered',
  'Installed',
];

// Trims, drops blanks, and dedupes the raw settings value case-insensitively.
export function normalizeCustomStatuses(raw) {
  const seen = new Set();
  const out = [];
  for (const entry of Array.isArray(raw) ? raw : []) {
    const label = String(entry ?? '').trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

/**
 * Builds the option list for a status <select>: the built-ins for that surface,
 * then the account's custom statuses.
 *
 * `currentValue` is the status already saved on the record. It is added when it
 * isn't otherwise in the list — a custom status that was later removed from
 * settings, or legacy data — because a <select> whose value has no matching
 * option renders blank and the next save silently rewrites the record's status.
 */
export function withCustomStatuses(builtInStatuses, customStatuses, currentValue) {
  const seen = new Set();
  const options = [];
  const add = (raw) => {
    const label = String(raw ?? '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    options.push(label);
  };

  (Array.isArray(builtInStatuses) ? builtInStatuses : []).forEach(add);
  normalizeCustomStatuses(customStatuses).forEach(add);

  const current = String(currentValue ?? '').trim();
  if (current && !options.includes(current)) options.push(current);

  return options;
}
